import { create } from 'zustand';
import { practiceApi } from '../lib/practiceApi';
import { contestApi } from '../lib/contestApi';
import { serverNow } from './serverClock';

const IDLE = { status: 'idle', data: null, error: null };
const inflight = new Map();

function once(key, task) {
  if (!inflight.has(key)) {
    inflight.set(
      key,
      task().finally(() => inflight.delete(key))
    );
  }
  return inflight.get(key);
}

export function todayIso() {
  return new Date(serverNow()).toISOString().slice(0, 10);
}

function walkStreak(calendar, today) {
  const entries = calendar.filter((entry) => entry.date <= today).sort((a, b) => b.date.localeCompare(a.date));
  let streak = 0;
  for (const entry of entries) {
    if (entry.solved) streak += 1;
    else if (entry.date === today) continue;
    else return { streak, complete: true };
  }
  return { streak, complete: false };
}

function previousMonth(year, month) {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

function settle(previous, response) {
  if (response.success) return { status: 'ready', data: response.data, error: null };
  if (previous.data) return { ...previous, status: 'ready' };
  return { status: 'error', data: null, error: response.error };
}

export const useCatalog = create((set, get) => ({
  problems: IDLE,
  contests: IDLE,
  daily: IDLE,

  loadProblems: () =>
    once('problems', async () => {
      set((state) => ({ problems: { ...state.problems, status: state.problems.data ? 'ready' : 'loading' } }));
      const response = await practiceApi.problems();
      set((state) => ({ problems: settle(state.problems, response) }));
    }),

  loadContests: () =>
    once('contests', async () => {
      set((state) => ({ contests: { ...state.contests, status: state.contests.data ? 'ready' : 'loading' } }));
      const response = await contestApi.list();
      set((state) => ({
        contests: settle(state.contests, response.success ? { success: true, data: response.data.contests } : response),
      }));
    }),

  loadDaily: () =>
    once('daily', async () => {
      set((state) => ({ daily: { ...state.daily, status: state.daily.data ? 'ready' : 'loading' } }));
      const today = todayIso();
      const [year, month] = today.split('-').map(Number);
      const response = await practiceApi.daily(year, month);
      if (!response.success) {
        set((state) => ({ daily: settle(state.daily, response) }));
        return;
      }
      let walked = walkStreak(response.data.calendar, today);
      if (!walked.complete) {
        const prior = previousMonth(year, month);
        const before = await practiceApi.daily(prior.year, prior.month);
        if (before.success) walked = walkStreak([...response.data.calendar, ...before.data.calendar], today);
      }
      set({
        daily: {
          status: 'ready',
          error: null,
          data: { ...response.data, year, month, todayIso: today, streak: walked.streak },
        },
      });
    }),

  patchContest: (contestId, patch) =>
    set((state) =>
      state.contests.data
        ? {
            contests: {
              ...state.contests,
              data: state.contests.data.map((contest) => (contest.id === contestId ? { ...contest, ...patch } : contest)),
            },
          }
        : {}
    ),

  markSolved: (problemId) => {
    const { problems, daily } = get();
    if (problems.data) {
      set({
        problems: {
          ...problems,
          data: problems.data.map((problem) => (problem.id === problemId ? { ...problem, status: 'solved' } : problem)),
        },
      });
    }
    if (daily.data?.today?.questionId === problemId) get().loadDaily();
  },

  reset: () => {
    inflight.clear();
    set({ problems: IDLE, contests: IDLE, daily: IDLE });
  },
}));

export function liveRegisteredContest(contests, now) {
  return (contests ?? []).find((contest) => contest.state === 'live' && contest.registered && Date.parse(contest.endTime) > now) ?? null;
}

export function featuredContest(contests) {
  const list = contests ?? [];
  const live = list.find((contest) => contest.state === 'live');
  if (live) return live;
  return (
    list
      .filter((contest) => contest.state === 'upcoming')
      .sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime))[0] ?? null
  );
}
